import { useContext, useMemo } from "react";
import { captureMessage } from "@sentry/browser";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";
import { z } from "zod";
import { FEATURE_FLAG_NAME, HELPSCOUT_BEACON_PAGE } from "common/constants";
import { buildLink, getPath } from "common/routing";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import { getInteractiveLayerIds } from "common-client/utils/customMapLayer";
import {
  isCustomMapGeometryResult,
  isPropertySearchResult,
  pointForResult,
} from "common-client/utils/search";
import {
  MapsForAccountQuery,
  OmniboxSearchResult,
  useGetOrCreateGuestPropertyLazyQuery,
} from "../../generated/graphql";
import { useLoadMapIcons } from "../../hooks/useMapIcons";
import { useMapPatterns } from "../../hooks/useMapPatterns";
import useURLParsing from "../../hooks/useURLParsing";
import { setTitle } from "../../utils/title";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { Container } from "../Maps/__styles__/FullPageMap";
import FullPageMap from "../Maps/FullPageMap";
import { ClickObjectMenuLayerConfig } from "../Maps/LayeredMap/clickObjectMenuLayer";
import {
  CustomMapLayerConfig,
  CustomMapLayerValue,
} from "../Maps/LayeredMap/customMapLayer";
import { ParcelLayerConfig } from "../Maps/LayeredMap/parcelLayer";
import { PropertyLayerConfig } from "../Maps/LayeredMap/propertyLayer";
import { SavedViewsLayerConfig } from "../Maps/LayeredMap/savedViewsLayer";
import { Point } from "../Maps/LayeredMap/types";
import { LayerId } from "../Maps/LayeredMap/types";
import { LayerContext } from "../Maps/layers";
import { isCustomMap, isProperty } from "../Maps/Map";
import { MapViewportContextProvider } from "../Maps/utils/MapViewportContext";
import { Bounds } from "../Maps/utils/viewportHook";
import { SearchContextProvider } from "../Search/SearchContext";
import {
  accountTitle,
  useGuestNavigation,
  useGuestSearchScopeBoundary,
  useHelpscoutBeacon,
} from "./utils";

const exploreParamsSchema = z.object({
  lat: z.coerce.number(),
  lng: z.coerce.number(),
});

export const GuestMap = ({
  account,
}: {
  account: NonNullable<MapsForAccountQuery["account"]>;
}) => {
  const loadMapIcons = useLoadMapIcons();
  const floodzonePatterns = useMapPatterns();

  const history = useHistory<{ prevLocation: string }>();
  const location = useLocation();
  const { t } = useTranslation();

  const { visibleSavedViews: getVisibleSavedViews } = useContext(LayerContext);
  const { account: authAccount, isFeatureEnabled } = useContext(AuthContext);
  const customMaps = authAccount?.customMaps ?? [];
  const publicCustomObjectsEnabled = isFeatureEnabled(
    FEATURE_FLAG_NAME.PUBLIC_CUSTOM_OBJECTS,
  );
  const guestSearchScopeBoundary = useGuestSearchScopeBoundary();

  const exploreParams = useURLParsing({
    routeName: "explore",
    parser: exploreParamsSchema,
  });
  const latitude = exploreParams?.lat;
  const longitude = exploreParams?.lng;

  const [getOrCreateProperty] = useGetOrCreateGuestPropertyLazyQuery({
    onCompleted: propertyData => {
      if (propertyData.getProperty) {
        navigateToPPP(propertyData.getProperty.id, {
          latitude: propertyData.getProperty.latitude!,
          longitude: propertyData.getProperty.longitude!,
        });
      } else {
        history.push({
          pathname: buildLink("noProperty"),
        });
      }
    },
  });

  useHelpscoutBeacon(HELPSCOUT_BEACON_PAGE.EXPLORE_MAP);

  const prevLocation =
    history.location.state?.prevLocation || getPath("guestHome");

  const handleExitClick = () => {
    history.push(prevLocation);
  };

  const {
    navigateToPPP: sharedNavigateToPPP,
    navigateToCustomObject: sharedNavigateToCustomObject,
  } = useGuestNavigation();

  const getExplorePrevLocation = (point?: Point) => {
    const params = queryString.parse(location.search);
    const firmId = params.firmId as string;

    const prev = buildLink(
      "explore",
      {},
      {
        lat: point ? point.latitude.toString() : (params.lat as string),
        lng: point ? point.longitude.toString() : (params.lng as string),
        firmId,
      },
    );

    history.replace(prev);
    return prev;
  };

  const navigateToPPP = (propertyId: string, point: Point) => {
    const prev = getExplorePrevLocation(point);
    sharedNavigateToPPP(propertyId, prev);
  };

  const navigateToCustomObject = (customMapId: string, geometryId: string) => {
    const prev = getExplorePrevLocation();
    sharedNavigateToCustomObject(customMapId, geometryId, prev);
  };

  const onParcelClick: ParcelLayerConfig["onClick"] = parcels => {
    // we should have exactly one parcel
    // because of the disambiguation menu layer
    if (!arrayHasAtLeastOneItem(parcels)) {
      captureMessage("Guest map clicked on zero parcels");
      return;
    } else if (parcels.length > 1) {
      captureMessage("Guest map clicked on multiple parcels", {
        extra: {
          parcels,
        },
      });
    }
    const parcel = parcels[0];

    track("Property clicked", { guest: true });

    // If parcel has one property, regardless of address, go to PPP
    if (parcel.propertyIds?.length === 1) {
      navigateToPPP(parcel.propertyIds[0]!, parcel.point);
      return;
    }

    // If parcel has address, find (or create), then to PPP
    if (parcel.address) {
      void getOrCreateProperty({
        variables: {
          parcelId: parcel.id,
          address: parcel.fullAddress,
          longitude: parcel.point.longitude,
          latitude: parcel.point.latitude,
        },
      });
      return;
    }

    // If parcel has no address AND many properties, pick first and go to PPP
    if (parcel.propertyIds?.length) {
      navigateToPPP(parcel.propertyIds[0]!, parcel.point);
      return;
    }

    // If parcel has no address AND no properties, (find or) create, then to PPP
    void getOrCreateProperty({
      variables: {
        parcelId: parcel.id,
        longitude: parcel.point.longitude,
        latitude: parcel.point.latitude,
      },
    });
  };

  const handleSearchResultClick = (result: OmniboxSearchResult) => {
    if (isCustomMapGeometryResult(result)) {
      if (publicCustomObjectsEnabled) {
        navigateToCustomObject(result.customMapId, result.id);
      }
      return;
    }
    track("Property clicked", { guest: true, search: true });
    const point = pointForResult(result);
    const propertyId = isPropertySearchResult(result) ? result.id : undefined;
    const parcelId = isPropertySearchResult(result)
      ? result.geocacheParcelId
      : result.id;
    void getOrCreateProperty({
      variables: {
        address: result.address ?? result.streetAddress,
        latitude: point.latitude,
        longitude: point.longitude,
        id: propertyId,
        parcelId,
      },
    });
  };

  const onSavedViewClick: SavedViewsLayerConfig["onClick"] = object => {
    if (isProperty(object)) {
      navigateToPPP(object.id, object.point);
    } else if (isCustomMap(object)) {
      if (
        publicCustomObjectsEnabled &&
        object.customMapId &&
        object.geometryId
      ) {
        navigateToCustomObject(object.customMapId, object.geometryId);
      }
    }
  };
  const onPropertyClick: PropertyLayerConfig["onClick"] = property => {
    navigateToPPP(property.id, property.point);
  };

  setTitle(`${t("tab-explore")} | ${accountTitle()}`);

  const point = useMemo(
    () => (latitude && longitude ? { latitude, longitude } : null),
    [latitude, longitude],
  );

  const customMapInteractiveLayerIds = getInteractiveLayerIds(
    customMaps,
  ) as Array<LayerId>;

  const parcelConfig = {
    interactive: { hover: true, click: true },
    onClick: onParcelClick,
    customMapInteractiveLayerIds,
    value: { point },
  } satisfies ParcelLayerConfig;
  const propertyConfig = {
    interactive: {
      hover: true,
      click: true,
    },
    onClick: onPropertyClick,
    customMapInteractiveLayerIds,
  } satisfies PropertyLayerConfig;

  const customMapsConfig = {
    interactive: {
      hover: publicCustomObjectsEnabled,
      click: publicCustomObjectsEnabled,
    },
    maps: customMaps,
    ...(publicCustomObjectsEnabled && {
      onClick: (object: CustomMapLayerValue) => {
        if (object.customMapId && object.geometryId) {
          navigateToCustomObject(object.customMapId, object.geometryId);
        }
      },
    }),
  } satisfies CustomMapLayerConfig;

  const visibleSavedViews = getVisibleSavedViews();

  const savedViewsConfig = {
    onClick: onSavedViewClick,
    value: visibleSavedViews,
    interactive: {
      hover: !!visibleSavedViews?.length,
      click: !!visibleSavedViews?.length,
    },
    savedViews: account.savedViews,
  } satisfies SavedViewsLayerConfig;

  const clickObjectMenuConfig = {
    interactive: { click: true, hover: true },
    maps: publicCustomObjectsEnabled ? customMapsConfig.maps : [],
    savedViews: publicCustomObjectsEnabled ? savedViewsConfig.value : [],
  } satisfies ClickObjectMenuLayerConfig;

  return (
    <Container>
      <MapViewportContextProvider
        initialViewport={
          point ?? {
            bounds: account.bounds as Bounds,
          }
        }
        refsToCompose={[loadMapIcons, floodzonePatterns]}
      >
        <SearchContextProvider
          handleSearchResultClick={handleSearchResultClick}
          enableRecentSearches
          showScopeBar
          calculateBounds
          scopeBoundary={guestSearchScopeBoundary}
        >
          <FullPageMap
            account={account}
            parcelConfig={parcelConfig}
            propertyConfig={propertyConfig}
            savedViewsConfig={savedViewsConfig}
            customMapsConfig={customMapsConfig}
            clickObjectMenuConfig={clickObjectMenuConfig}
            handleExitClick={handleExitClick}
          />
        </SearchContextProvider>
      </MapViewportContextProvider>
    </Container>
  );
};
